import {
  PLATFORM_CAPABILITIES,
  PLATFORM_CAPABILITY_DESCRIPTIONS,
  PLATFORM_CAPABILITY_LABELS,
  PLATFORM_TEAM_ADMIN_CAPABILITIES,
  type PlatformCapability,
  type PlatformInvitation,
  type PlatformTeamMember,
} from '@lia/contracts';
import { EMPTY, formatDateTime } from '../../lib/format.js';
import { Badge, StatusBadge } from '../primitives/badge.js';
import { Button } from '../primitives/button.js';
import { Checkbox, Switch } from '../primitives/checkbox.js';
import { EmptyState } from '../primitives/states.js';
import { DataTable, type DataTableColumn } from '../layout/data-table.js';

// «Plataforma → Equipo Estateflow»: presentational pieces (screens wire the API). One switch per platform capability;
// the signed-in super operator's own admin switches are locked (no self lock-out); pending invitations with revoke.

const ADMIN = PLATFORM_TEAM_ADMIN_CAPABILITIES as readonly PlatformCapability[];

/** Whether a switch is locked for this member: own super-operator capabilities cannot be removed by oneself. */
export function isCapabilityLocked(member: Pick<PlatformTeamMember, 'isSelf' | 'superOperator'>, capability: PlatformCapability): boolean {
  return member.isSelf && member.superOperator && ADMIN.includes(capability);
}

/** Legend: the Spanish label and one-line explanation of each capability. */
export function PlatformCapabilityLegend({ className }: { className?: string }) {
  return (
    <dl className={className ?? 'grid gap-2 sm:grid-cols-2'}>
      {PLATFORM_CAPABILITIES.map((c) => (
        <div key={c} className="grid gap-0.5 rounded-control border border-border-subtle bg-surface-default px-3 py-2">
          <dt className="text-body font-medium text-text-primary">{PLATFORM_CAPABILITY_LABELS[c]}</dt>
          <dd className="text-meta text-text-secondary">{PLATFORM_CAPABILITY_DESCRIPTIONS[c]}</dd>
        </div>
      ))}
    </dl>
  );
}

export interface PlatformCapabilityChecklistProps {
  value: readonly PlatformCapability[];
  onChange: (next: PlatformCapability[]) => void;
  disabled?: boolean;
}

/** Checkbox list (label + explanation) to choose capabilities, e.g. in «Agregar persona». */
export function PlatformCapabilityChecklist({ value, onChange, disabled }: PlatformCapabilityChecklistProps) {
  return (
    <div className="grid gap-3" role="group" aria-label="Permisos de plataforma">
      {PLATFORM_CAPABILITIES.map((c) => (
        <Checkbox
          key={c}
          label={PLATFORM_CAPABILITY_LABELS[c]}
          description={PLATFORM_CAPABILITY_DESCRIPTIONS[c]}
          checked={value.includes(c)}
          disabled={disabled}
          onCheckedChange={(checked) => onChange(PLATFORM_CAPABILITIES.filter((x) => (x === c ? checked === true : value.includes(x))))}
        />
      ))}
    </div>
  );
}

export interface PlatformTeamTableProps {
  members: readonly PlatformTeamMember[];
  /** A switch was flipped; the screen confirms revokes and calls the API. */
  onToggle: (member: PlatformTeamMember, capability: PlatformCapability, next: boolean) => void;
  /** `${userId}:${capability}` of the change being saved (its switch is disabled meanwhile). */
  pendingKey?: string | null;
  timeZone: string;
  loading?: boolean;
}

/** People with any platform capability, one switch per capability. */
export function PlatformTeamTable({ members, onToggle, pendingKey, timeZone, loading }: PlatformTeamTableProps) {
  const columns: DataTableColumn<PlatformTeamMember>[] = [
    {
      id: 'person',
      header: 'Persona',
      className: 'min-w-56',
      cell: (m) => (
        <div className="grid gap-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium text-text-primary">{m.name}</span>
            {m.isSelf ? <Badge tone="accent">Tú</Badge> : null}
            {m.superOperator ? <Badge tone="outline">Superoperador</Badge> : null}
          </div>
          <span className="text-meta text-text-secondary">{m.email}</span>
          <div className="flex flex-wrap gap-1.5">
            {m.disabledAt ? <StatusBadge tone="danger">Cuenta desactivada</StatusBadge> : null}
            {m.mfaEnabled ? <StatusBadge tone="success">Verificación en dos pasos</StatusBadge> : <StatusBadge tone="warning">Sin verificación en dos pasos</StatusBadge>}
          </div>
        </div>
      ),
    },
    ...PLATFORM_CAPABILITIES.map(
      (c): DataTableColumn<PlatformTeamMember> => ({
        id: c,
        header: PLATFORM_CAPABILITY_LABELS[c],
        align: 'center',
        cell: (m) => {
          const locked = isCapabilityLocked(m, c);
          return (
            <div className="grid justify-items-center gap-1">
              <Switch
                aria-label={`${PLATFORM_CAPABILITY_LABELS[c]} — ${m.name}`}
                checked={m.capabilities.includes(c)}
                disabled={locked || pendingKey === `${m.userId}:${c}`}
                title={locked ? 'No puedes quitarte tus propios permisos de superoperador' : undefined}
                onCheckedChange={(next) => onToggle(m, c, next)}
              />
              {c === 'platform_mcp' && m.mcpConnections > 0 ? (
                <span className="text-meta text-text-secondary">
                  {m.mcpConnections === 1 ? '1 conexión activa' : `${m.mcpConnections} conexiones activas`}
                </span>
              ) : null}
            </div>
          );
        },
      }),
    ),
    {
      id: 'lastLogin',
      header: 'Último acceso',
      hideOnMobile: true,
      cell: (m) => <span className="text-meta text-text-secondary">{m.lastLoginAt ? formatDateTime(m.lastLoginAt, timeZone) : EMPTY}</span>,
    },
  ];
  return (
    <DataTable
      columns={columns}
      rows={members}
      getRowId={(m) => m.userId}
      caption="Equipo Estateflow: permisos de plataforma por persona"
      loading={loading}
      empty={<EmptyState compact title="Nadie tiene permisos de plataforma" description="Agrega a una persona del equipo para darle permisos." />}
      footer={
        members.some((m) => m.isSelf && m.superOperator) ? (
          <p className="text-meta text-text-secondary">
            Tus permisos de superoperador (instrucciones globales, modelos e integraciones) no se pueden quitar desde tu propia cuenta: pídeselo a otra persona del equipo.
          </p>
        ) : null
      }
    />
  );
}

export interface PlatformInvitationListProps {
  invitations: readonly PlatformInvitation[];
  onRevoke: (invitation: PlatformInvitation) => void;
  revokingId?: string | null;
  timeZone: string;
}

/** Pending (or expired, not yet replaced) platform invitations. */
export function PlatformInvitationList({ invitations, onRevoke, revokingId, timeZone }: PlatformInvitationListProps) {
  if (!invitations.length) return <p className="text-body text-text-secondary">No hay invitaciones pendientes.</p>;
  return (
    <ul className="grid gap-2" aria-label="Invitaciones pendientes">
      {invitations.map((inv) => (
        <li key={inv.id} className="flex flex-wrap items-center justify-between gap-3 rounded-control border border-border-subtle bg-surface-default px-3 py-2">
          <div className="grid min-w-0 gap-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="truncate font-medium text-text-primary">{inv.email}</span>
              {inv.status === 'expired' ? <StatusBadge tone="warning">Vencida</StatusBadge> : <StatusBadge tone="info">Pendiente</StatusBadge>}
            </div>
            <span className="text-meta text-text-secondary">
              {inv.capabilities.map((c) => PLATFORM_CAPABILITY_LABELS[c]).join(' · ')}
            </span>
            <span className="text-meta text-text-secondary">
              {inv.invitedByName ? `Invitó ${inv.invitedByName} · ` : ''}
              {inv.status === 'expired' ? 'Venció' : 'Vence'} {formatDateTime(inv.expiresAt, timeZone)}
            </span>
          </div>
          <Button variant="secondary" size="sm" loading={revokingId === inv.id} onClick={() => onRevoke(inv)} aria-label={`Revocar invitación de ${inv.email}`}>
            Revocar
          </Button>
        </li>
      ))}
    </ul>
  );
}
